import * as React from "react"
import { CalendarDays } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import moment from "moment"
import { cn } from "@/utils/shadcn-utils"

function formatDate(date: Date | undefined) {
  if (!date) {
    return ""
  }

  return moment(date).format("YYYY-MM-DD")
  // date.toLocaleDateString("fr-FR", {
  //   day: "2-digit",
  //   month: "2-digit",
  //   year: "numeric",
  // })
}

function parseDate(dateString: string): Date | undefined {
  if (!dateString) return undefined

  const datePattern = /^(\d{4})-(\d{2})-(\d{2})$/
  const match = dateString.match(datePattern)

  if (match) {
    const day = parseInt(match[3], 10)
    const month = parseInt(match[2], 10) - 1 // Les mois sont 0-indexés
    const year = parseInt(match[1], 10)

    const date = new Date(year, month, day)

    // Vérifier que la date est valide
    if (
      date.getDate() === day &&
      date.getMonth() === month &&
      date.getFullYear() === year
    ) {
      return date
    }
  }

  return undefined
}

export interface CustomDatePickerProps {
  name: string
  label?: string
  value: string
  placeholder?: string
  disabled?: boolean
  onChange: (value: string) => void
  minDate?: Date | undefined
}

export default function CustomDatePicker(props : CustomDatePickerProps) {
  const {
    name,
    label,
    value,
    placeholder,
    disabled = false,
    onChange,
    minDate,
  } = props

  const [open, setOpen] = React.useState(false)
  const [date, setDate] = React.useState<Date | undefined>(undefined)
  const [month, setMonth] = React.useState<Date | undefined>(undefined)
  const [inputValue, setInputValue] = React.useState(value)

  // Synchroniser avec la valeur externe si elle change
  React.useEffect(() => {
    if (value) {
      const parsedDate = parseDate(value)
      setDate(parsedDate)
      setInputValue(formatDate(parsedDate))
      if (parsedDate) {
        setMonth(parsedDate)
      }
    }
  }, [value])

  return (
    <div className="flex flex-col gap-3">
      <Label htmlFor={name} className="px-1 font-normal">
        {label}
      </Label>
      <div className="relative flex gap-2">
        <Input
          id={name}
          value={inputValue}
          placeholder={placeholder ?? "AAAA-JJ-MM"}
          className="bg-background pr-10 relative flex gap-2"
          disabled={disabled}
          onChange={(e) => {
            const newValue = e.target.value
            setInputValue(newValue)

            // Tenter de parser la date seulement si le format semble complet
            if (newValue.length === 10) {
              const parsedDate = parseDate(newValue)
              if (parsedDate) {
                setDate(parsedDate)
                setMonth(parsedDate)
                onChange(formatDate(parsedDate))
              }
            }
          }}
          onBlur={() => {
            // Valider la date au blur
            if (inputValue) {
              const parsedDate = parseDate(inputValue)
              if (parsedDate) {
                setDate(parsedDate)
                setMonth(parsedDate)
                onChange(formatDate(parsedDate))
              } else {
                // Réinitialiser si la date n'est pas valide
                setInputValue(formatDate(date))
              }
            } else {
              // Si vide, réinitialiser
              setDate(undefined)
              onChange("")
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault()
              setOpen(true)
            }
          }}
        />
        <Popover
          open={open}
          onOpenChange={() => {
            if (!disabled) {
              setOpen(!open)
            }
          }}>
          <PopoverTrigger
            asChild
            className={
              cn("absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer", disabled && "cursor-not-allowed")
            }>
            <CalendarDays size={18} />
          </PopoverTrigger>
          <PopoverContent
            className="w-70 overflow-hidden p-0 z-50"
            align="end"
            alignOffset={-8}
            sideOffset={10}>
            <Calendar
              mode="single"
              selected={date}
              // captionLayout="dropdown"
              month={month}
              fromDate={minDate ?? new Date("2000-01-01")}
              onMonthChange={setMonth}
              onSelect={(selectedDate) => {
                setDate(selectedDate)
                setInputValue(formatDate(selectedDate))
                onChange(formatDate(selectedDate))
                setOpen(false)
              }}
              className="flex m-auto rounded-md"
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
// import * as React from "react"
// import { CalendarDays, CalendarIcon } from "lucide-react"
//
// import { Button } from "@/components/ui/button"
// import { Calendar } from "@/components/ui/calendar"
// import { Input } from "@/components/ui/input"
// import { Label } from "@/components/ui/label"
// import {
//   Popover,
//   PopoverContent,
//   PopoverTrigger,
// } from "@/components/ui/popover"
// import { CommandDialog } from "components/ui/command"
//
// function formatDate(date: Date | undefined) {
//   if (!date) {
//     return ""
//   }
//
//   return date.toLocaleDateString("fr-FR", {
//     day: "2-digit",
//     month: "2-digit",
//     year: "numeric",
//   })
// }
//
// function isValidDate(date: Date | undefined) {
//   if (!date) {
//     return false
//   }
//   return !isNaN(date.getTime())
// }
//
// export default function CustomDatePicker(props) {
//   const {
//     name,
//     label,
//     value,
//     placeholder,
//     onChange,
//     minDate,
//   } = props
//   const [open, setOpen] = React.useState(false)
//   const [date, setDate] = React.useState<Date | undefined>(
//     new Date()
//   )
//   const [month, setMonth] = React.useState<Date | undefined>(date)
//
//   return (
//     <div className="flex flex-col gap-3">
//       <Label htmlFor={name} className="px-1 font-normal">
//         {label}
//       </Label>
//       <div className="relative flex gap-2">
//         <Input
//           id={name}
//           value={formatDate(date)}
//           placeholder={placeholder ?? ""}
//           className="bg-background pr-10"
//           onChange={(e) => {
//             const date = new Date(e.target.value)
//             // console.log(date.toLocaleDateString("fr-FR", {}))
//             if (isValidDate(date)) {
//               setDate(date)
//               onChange(formatDate(date))
//               setMonth(date)
//             } else{
//             }
//           }}
//           onKeyDown={(e) => {
//             if (e.key === "ArrowDown") {
//               e.preventDefault()
//               setOpen(true)
//             }
//           }}
//         />
//         <Popover open={open} onOpenChange={setOpen}>
//           <PopoverTrigger asChild>
//             <Button
//               id={`date-picker-${name}`}
//               variant="ghost"
//               className="absolute top-1/2 right-2 size-6 -translate-y-1/2">
//               <CalendarDays  className="size-3.5"/>
//               <span className="sr-only">Selectionnez une date</span>
//             </Button>
//           </PopoverTrigger>
//           <PopoverContent
//             className="w-70 overflow-hidden p-0 z-50"
//             align="end"
//             alignOffset={-8}
//             sideOffset={10}>
//             {/*<CommandDialog open={open} onOpenChange={setOpen} >*/}
//               <Calendar
//                 mode="single"
//                 selected={date}
//                 captionLayout="dropdown"
//                 month={month}
//                 minDate={minDate ?? new Date("01/01/2000")}
//                 onMonthChange={setMonth}
//                 onSelect={(date) => {
//                   setDate(date)
//                   // console.log(date?.toLocaleDateString("fr-FR", {}))
//                   onChange(formatDate(date))
//                   setOpen(false)
//                 }}
//                 className="flex m-auto rounded-md "
//               />
//             {/*</CommandDialog>*/}
//           </PopoverContent>
//         </Popover>
//       </div>
//     </div>
//   )
// }
